import { ActionCreator, Dispatch } from "redux";
import { ThunkAction } from "redux-thunk";
import { isEmpty } from "lodash";

import { ApplicationState } from "../redux/ApplicationState";
import {
  EligibilityCheck,
  EligibilityInformation,
  GetPatient,
  GetPatientInformationPayload,
  LogContractIdPbpPayload,
  MemberInfoData,
  SaveInformation,
} from "../models/Patient";
import {
  BenefitPlanUidDispatchTypes,
  CreatePatientDispatchTypes,
  EligibilityCheckDispatchTypes,
  EligibilityInformationDispatchTypes,
  IcdCodeDispatchTypes,
  InformationDispatchTypes,
  LogContractIdPBPDetailsDispatchTypes,
  MemberInfoDataDispatchTypes,
  PaientFromMbiDispatchTypes,
  PatientInformationByIdDispatchTypes,
  ReAuthDefaultValueDispatchTypes,
} from "../models/dispatchTypes/Patient";
import {
  BenefitPlanUidActionDispatchTypes,
  CreatePatientActionDispatchTypes,
  EligibilityCheckActionDispatchTypes,
  IcdCodeActionDispatchTypes,
  InformationActionDispatchTypes,
  LogContractIdPBPDetailsActionDispatchTypes,
  MemberInfoDataActionDispatchTypes,
  PatientFromMbiActionDispatchTypes,
  PatientInformationByIdActionDispatchTypes,
  ReAuthDefaultValueActionDispatchTypes,
} from "../constants/Patient";
import { configUrl } from "../constants/Config";
import {
  CheckEligibility,
  CreatePatient,
  GetHealthPlanDetailsByBenefitPlanUid,
  GetIcdCode,
  GetPatientFromMBI,
  GetPatientInformationById,
  Information,
  LogContractIdPBPDetails,
} from "../services/Patient";
import toastType from "../utils/SnackbarProvider";
import { MessageType, ResponseMessages } from "../constants/ResponseMessage";
import { ReferralDetail } from "../models/Referral";
import { EligibilityInformationActionDispatchTypes } from "../constants/eligibility";
import { NoMncDetailsActionDispatchTypes } from "../constants/Referral";
import { UploadNoMNCDocumentDetails } from "../routes/HomeScreen/components/NoMncModal";
import { NoMncDetailsDispatchTypes } from "../models/dispatchTypes/Referral";
import { PutUploadNoMNCDocumentDetails } from "../services/Referral";

export const checkEligibility: ActionCreator<
  ThunkAction<
    void,
    ApplicationState,
    Record<string, never>,
    EligibilityCheckDispatchTypes
  >
> = (payload: EligibilityCheck) => {
  return async (dispatch: Dispatch) => {
    dispatch({
      type: EligibilityCheckActionDispatchTypes.ELIGIBILITY_CHECK_INVOKE,
    });
    const response = await CheckEligibility(configUrl, payload);
    if (response && response.status) {
      switch (response.status) {
        case 422:
        case 400:
        case 500:
        case 404:
          toastType.error(
            ResponseMessages(MessageType.ELIGIBILITY_FAILURE)
          );
          break;
        case 200:
        case 201:
          dispatch({
            response: response.data,
            type: EligibilityCheckActionDispatchTypes.ELIGIBILITY_CHECK_SUCCESS,
          });
          break;
        default:
          break;
      }
    }
  };
};

export const getPatientFromMBI: ActionCreator<
  ThunkAction<
    void,
    ApplicationState,
    Record<string, never>,
    PaientFromMbiDispatchTypes
  >
> = (payload: GetPatient) => {
  return async (dispatch: Dispatch) => {
    dispatch({
      type: PatientFromMbiActionDispatchTypes.PATIENT_FROM_MBI_INVOKE,
    });
    const response = await GetPatientFromMBI(configUrl, payload);
    dispatch({
      response: response.data,
      type: PatientFromMbiActionDispatchTypes.PATIENT_FROM_MBI_SUCCESS,
    });
  };
};

export const createPatient: ActionCreator<
  ThunkAction<
    void,
    ApplicationState,
    Record<string, never>,
    CreatePatientDispatchTypes
  >
> = (payload: FormData) => {
  return async (dispatch: Dispatch) => {
    dispatch({
      type: CreatePatientActionDispatchTypes.CREATE_PATIENT_INVOKE,
    });
    const response = await CreatePatient(configUrl, payload);
    if (response && response.status) {
      switch (response.status) {
        case 400:
           toastType.error(
            ResponseMessages(MessageType.CREATE_PATIENT_FAILURE)
          );
          dispatch({
            error: response,
            type: CreatePatientActionDispatchTypes.CREATE_PATIENT_ERRORS,
          });
          break;
        case 422:
        case 500:
        case 404:
          toastType.error(
            ResponseMessages(MessageType.CREATE_PATIENT_FAILURE)
          );
          break;
        case 200:
        case 201:
          dispatch({
            response: response.data,
            type: CreatePatientActionDispatchTypes.CREATE_PATIENT_SUCCESS,
          });
          break;
        default:
          break;
      }
    }
  };
};

export const information: ActionCreator<
  ThunkAction<
    void,
    ApplicationState,
    Record<string, never>,
    InformationDispatchTypes
  >
> = (payload: SaveInformation) => {
  return async (dispatch: Dispatch) => {
    dispatch({
      type: InformationActionDispatchTypes.INFORMATION_INVOKE,
    });
    const response = await Information(configUrl, payload);
    if (response && response.status) {
      switch (response.status) {
        case 400:
           dispatch({
            error: response,
            type: InformationActionDispatchTypes.INFORMATION_ERROR,
           });
           break;
        case 500:
        case 422:
        case 404:
          toastType.error(
            ResponseMessages(MessageType.INFORMATION_FAILURE)
          );
          break;
        case 200:
        case 201:
          dispatch({
            response: response.data,
            type: InformationActionDispatchTypes.INFORMATION_SUCCESS,
          });
          break;
        default:
          break;
      }
    }
  };
};

export const patientInformationById: ActionCreator<
  ThunkAction<
    void,
    ApplicationState,
    Record<string, never>,
    PatientInformationByIdDispatchTypes
  >
> = (payload: GetPatientInformationPayload) => {
  return async (dispatch: Dispatch) => {
    dispatch({
      type: PatientInformationByIdActionDispatchTypes.PATIENT_INFORMATION_BY_ID_INVOKE,
    });
    const response = await GetPatientInformationById(configUrl, payload);
    if (response && response.status) {
      switch (response.status) {
        case 422:
        case 400:
        case 500:
        case 404:
          break;
        case 200:
        case 201:
          dispatch({
            response: response.data,
            type: PatientInformationByIdActionDispatchTypes.PATIENT_INFORMATION_BY_ID_SUCCESS,
          });
          break;
        default:
          break;
      }
    }
  };
};


export const getIcdCodes: ActionCreator<
  ThunkAction<
    void,
    ApplicationState,
    Record<string, never>,
    IcdCodeDispatchTypes
  >
> = (input: string, field: string) => {
  return async (dispatch: Dispatch) => {
    dispatch({
      type: IcdCodeActionDispatchTypes.ICD_CODE_INVOKE,
      field
    });
    const response = await GetIcdCode(configUrl, input);
    if (response && response.status) {
      switch (response.status) {
        case 422:
        case 400:
        case 404:
        case 500:
          dispatch({
            type: IcdCodeActionDispatchTypes.ICD_CODE_ERROR,
            field
          });
          break;
        case 200:
          dispatch({
            response: response.data,
            field,
            type: IcdCodeActionDispatchTypes.ICD_CODE_SUCCESS,
          });
          break;
        default:
          break;
      }
    }
  };
};

export const getHealthPlanDetailsByBenefitPlanUid: ActionCreator<
ThunkAction<
  void,
  ApplicationState,
  Record<string, never>,
  BenefitPlanUidDispatchTypes
>
> = (benefitPlanUid: string) => {
return async (dispatch: Dispatch) => {
  dispatch({
    type: BenefitPlanUidActionDispatchTypes.BENEFIT_PLAN_UID_INVOKE,
  });
  const response = await GetHealthPlanDetailsByBenefitPlanUid(configUrl, benefitPlanUid);
  if (response && response.status) {
    switch (response.status) {
      case 422:
      case 400:
      case 404:
      case 500:
        dispatch({
          type: BenefitPlanUidActionDispatchTypes.BENEFIT_PLAN_UID_ERRORS,
        });
        break;
      case 200:
        dispatch({
          response: response.data,
          type: BenefitPlanUidActionDispatchTypes.BENEFIT_PLAN_UID_SUCCESS,
        });
        break;
      default:
        break;
    }
  }
};
};

export const setDefaultInfoForReAuth: ActionCreator<
  ThunkAction<
    void,
    ApplicationState,
    Record<string, never>,
    ReAuthDefaultValueDispatchTypes
  >
> = (payload: ReferralDetail) => {
  return async (dispatch: Dispatch) => {
    dispatch({
      response: payload,
      type:ReAuthDefaultValueActionDispatchTypes.RE_AUTH_DEFAULT_INFO_ELIGIBILITY_CHECK_SUCCESS,
    });
  };
  };

  export const saveEligibilityInformation: ActionCreator<
  ThunkAction<
    void,
    ApplicationState,
    Record<string, never>,
    EligibilityInformationDispatchTypes
  >
> = (sessionData: EligibilityInformation) => {
  return async (dispatch: Dispatch) => {
    dispatch({
      type: EligibilityInformationActionDispatchTypes.SET_ELIGIBILITY_INFORMATION_INVOKE,
    });
    const response = sessionData
    sessionStorage.setItem("checkEligibilityResponse", JSON.stringify(response));
    dispatch({
      response: response,
      type: EligibilityInformationActionDispatchTypes.SET_ELIGIBILITY_INFORMATION_SUCCESS,
    });
  };
  };


  export const saveMemberInfo: ActionCreator<
    ThunkAction<
      void,
      ApplicationState,
      Record<string, never>,
      MemberInfoDataDispatchTypes
    >
  > = (payload: MemberInfoData) => {
    return async (dispatch: Dispatch) => {
      dispatch({
        response: payload,
        type: MemberInfoDataActionDispatchTypes.MEMBER_INFO_DATA_SUCCESS,
      });
    };
    };

  export const putUploadNoMNCDocumentDetails: ActionCreator<
    ThunkAction<
      void,
      ApplicationState,
      Record<string, never>,
      NoMncDetailsDispatchTypes
    >
  > = (payload: UploadNoMNCDocumentDetails) => {
    return async (dispatch: Dispatch) => {
      dispatch({
        type: NoMncDetailsActionDispatchTypes.NO_MNC_DOCUMENT_DETAILS_INVOKE,
      });
      const response = await PutUploadNoMNCDocumentDetails(configUrl, payload);
      if (isEmpty(response) || response === undefined) {
        toastType.error(ResponseMessages(MessageType.DOCUMENT_UPDATE_NOMNC_DETAILS_ERROR));
        dispatch({
          error: response,
          type: NoMncDetailsActionDispatchTypes.NO_MNC_DOCUMENT_DETAILS_ERROR,
        });
        return;
      }
      if (response && response.status) {
        switch (response.status) {
          case 422:
          case 400:
          case 404:
          case 500:
             toastType.error(
            response.data
          );
            dispatch({
              type: NoMncDetailsActionDispatchTypes.NO_MNC_DOCUMENT_DETAILS_ERROR,
            });
            break;
          case 200:
           response.data === true &&
             toastType.success(ResponseMessages(MessageType.FILE_UPLOADED_SUCCESSFULLY));
            dispatch({
              response: response,
              type: NoMncDetailsActionDispatchTypes.NO_MNC_DOCUMENT_DETAILS_SUCCESS,
            });
            break;
          default:
            break;
        }
      }
    };
  };


  export const logContractIdPBPDetails: ActionCreator<
    ThunkAction<
      void,
      ApplicationState,
      Record<string, never>,
      LogContractIdPBPDetailsDispatchTypes
    >
  > = (payload: LogContractIdPbpPayload) => {
    return async (dispatch: Dispatch) => {
      dispatch({
        type: LogContractIdPBPDetailsActionDispatchTypes.LOG_CONTRACT_ID_PBP_DETAILS_INVOKE,
      });
      const response = await LogContractIdPBPDetails(configUrl, payload);
      dispatch({
        response: response.data,
        type: LogContractIdPBPDetailsActionDispatchTypes.LOG_CONTRACT_ID_PBP_DETAILS_SUCCESS,
      });
    };
  };
